import AsyncAutocomplete from '@components/v2/form/AsyncAutocomplete/AsyncAutocomplete';
import MoneyField from '@components/v2/form/MoneyField/MoneyField';
import Button from '@components/v2/ui/Button/Button';
import { Box, Collapse, Stack } from '@mui/material';
import { useState } from 'react';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { ListCustomerAddressDTO } from '../../../../../../../../../types/address.types';
import { CustomerAddress } from '../../../../../../../../customer/types/customer.types';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { OrderCondition } from '../../../../../../../conditions/interfaces/orderCondition.interface';
import { FreightOrigin } from '../../../../../../../conditions/orderCondition.types';
import { getAddressOptionLabel } from '../../../../../../../conditions/orderCondition.utils';
import { OrderConditionFieldWithError } from '../../../../../../../conditions/orderConditionFieldErrors.map';
import { OrderConditionShippingFormVisibility } from '../../../orderObjectConditionsTab.types';
import OrderConditionFreightField from '../../OrderConditionFreightField/OrderConditionFreightField';
import { OrderConditionFreightSelection } from '../../OrderConditionFreightField/orderConditionFreightField.types';
import { blurAutocompleteOnPointerSelect, hasFieldError } from '../../orderConditionDetails.utils';
import useOrderConditionAutoQuote from '../hooks/useOrderConditionAutoQuote';
import { OrderConditionDetailsFormValues } from '../orderConditionDetailsForm.schema';
import { CLEARED_FREIGHT_VALUES } from '../orderConditionDetailsForm.utils';
import OrderConditionManualFreightFields from './OrderConditionManualFreightFields';

interface OrderConditionDeliveryFieldsProps {
  orderId: string;
  condition: OrderCondition;
  customerId?: string;
  control: Control<OrderConditionDetailsFormValues>;
  canEditOrder: boolean;
  containerWidth: number;
  fieldsWithError: Set<OrderConditionFieldWithError>;
  visibility: OrderConditionShippingFormVisibility;
  setValue: UseFormSetValue<OrderConditionDetailsFormValues>;
  onSave: () => void;
}

const OrderConditionDeliveryFields = ({
  orderId,
  condition,
  customerId,
  control,
  canEditOrder,
  containerWidth,
  fieldsWithError,
  visibility,
  setValue,
  onSave,
}: OrderConditionDeliveryFieldsProps) => {
  const { translate } = useTranslation();
  const [isQuoteRetryOpen, setIsQuoteRetryOpen] = useState(false);
  const { isQuoting, quoteAutomatically } = useOrderConditionAutoQuote({
    orderId,
    orderConditionId: condition.id,
    onQuoted: () => handleQuoteAvailable(),
  });
  const { isShippingCostVisible, isFreightQuoteVisible, isManualFreightReadOnly } = visibility;
  const [
    shippingCompanyName,
    shippingMethod,
    shippingCostValue,
    estimatedShippingBusinessDays,
    estimatedShippingDate,
    quoteId,
    freightOrigin,
  ] = useWatch({
    control,
    name: [
      'shippingCompanyName',
      'shippingMethod',
      'shippingCostValue',
      'estimatedShippingBusinessDays',
      'estimatedShippingDate',
      'quoteId',
      'freightOrigin',
    ],
  });

  const isManualFreight = freightOrigin === FreightOrigin.MANUAL;
  const isManualFreightFieldsVisible =
    isFreightQuoteVisible && isManualFreight && !isQuoteRetryOpen;
  const isFreightQuoteFieldVisible =
    isFreightQuoteVisible && (!isManualFreight || isQuoteRetryOpen);

  const clearFreightValues = (nextFreightOrigin: FreightOrigin | null) => {
    setValue('shippingCostValue', CLEARED_FREIGHT_VALUES.shippingCostValue);
    setValue('shippingCompanyId', CLEARED_FREIGHT_VALUES.shippingCompanyId);
    setValue('shippingCompanyName', CLEARED_FREIGHT_VALUES.shippingCompanyName);
    setValue('shippingCompanyExternalId', CLEARED_FREIGHT_VALUES.shippingCompanyExternalId);
    setValue('shippingMethod', CLEARED_FREIGHT_VALUES.shippingMethod);
    setValue('estimatedShippingBusinessDays', CLEARED_FREIGHT_VALUES.estimatedShippingBusinessDays);
    setValue('estimatedShippingDate', CLEARED_FREIGHT_VALUES.estimatedShippingDate);
    setValue('quoteId', CLEARED_FREIGHT_VALUES.quoteId);
    setValue('freightOrigin', nextFreightOrigin);
  };

  const handleQuoteAvailable = () => {
    setIsQuoteRetryOpen(true);

    if (isManualFreightReadOnly) {
      return;
    }

    clearFreightValues(null);
    onSave();
  };

  const handleManualFreightRequest = () => {
    setIsQuoteRetryOpen(false);
    clearFreightValues(FreightOrigin.MANUAL);
  };

  const handleFreightQuoteSelect = (selection: OrderConditionFreightSelection) => {
    setIsQuoteRetryOpen(false);
    setValue('shippingCostValue', selection.price, { shouldDirty: true });
    setValue('shippingCompanyId', null);
    setValue('shippingCompanyName', selection.shippingCompanyName);
    setValue('shippingCompanyExternalId', selection.shippingCompanyExternalId);
    setValue('shippingMethod', selection.shippingMethod);
    setValue('estimatedShippingBusinessDays', selection.estimatedShippingBusinessDays);
    setValue('estimatedShippingDate', selection.estimatedShippingDate);
    setValue('quoteId', selection.quoteId);
    setValue('freightOrigin', FreightOrigin.AUTOMATIC);

    onSave();
  };

  return (
    <Stack sx={{ gap: 2 }}>
      <Controller
        name="deliveryAddressId"
        control={control}
        render={({ field, fieldState }) => (
          <AsyncAutocomplete<CustomerAddress>
            source={{
              url: `/customers/${customerId}/address`,
              limit: 20,
              enabled: !!customerId,
            }}
            label={translate(LangEnum.ORDER_CONDITIONS_DELIVERY_ADDRESS_LABEL)}
            value={field.value ?? null}
            getOptionId={(option) => option.address?.id ?? option.id}
            getOptionLabel={(option) =>
              getAddressOptionLabel(option as unknown as ListCustomerAddressDTO)
            }
            initialData={
              condition.deliveryAddress
                ? ([
                    {
                      id: condition.deliveryAddress.id,
                      address: condition.deliveryAddress,
                      principal: false,
                    },
                  ] as unknown as CustomerAddress[])
                : []
            }
            disabled={!canEditOrder || !customerId}
            error={hasFieldError(fieldState, fieldsWithError, 'deliveryAddressId')}
            helperText={fieldState.error?.message}
            onBlur={field.onBlur}
            onClose={blurAutocompleteOnPointerSelect}
            onChange={(value) => {
              field.onChange(value);
              onSave();
            }}
          />
        )}
      />

      {isFreightQuoteVisible && (
        <Box>
          <Collapse in={isManualFreightFieldsVisible} unmountOnExit>
            <Stack sx={{ gap: 2 }}>
              <OrderConditionManualFreightFields
                condition={condition}
                control={control}
                canEditOrder={canEditOrder && !isManualFreightReadOnly}
                containerWidth={containerWidth}
                fieldsWithError={fieldsWithError}
                onSave={onSave}
              />

              <Box>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!canEditOrder}
                  loading={isQuoting}
                  onClick={quoteAutomatically}
                >
                  {translate(LangEnum.ORDER_CONDITION_FREIGHT_QUOTE_AGAIN)}
                </Button>
              </Box>
            </Stack>
          </Collapse>

          <Collapse in={isFreightQuoteFieldVisible} unmountOnExit>
            <Controller
              name="shippingCostValue"
              control={control}
              render={({ field, fieldState }) => (
                <OrderConditionFreightField
                  orderId={orderId}
                  orderConditionId={condition.id}
                  isDisabled={!canEditOrder}
                  canEnterManualFreight={!isManualFreightReadOnly}
                  hasError={hasFieldError(fieldState, fieldsWithError, 'shippingCostValue')}
                  hasPrefetchedQuotes={isQuoteRetryOpen}
                  onBlur={field.onBlur}
                  savedFreight={{
                    shippingCompanyName,
                    shippingMethod,
                    shippingCostValue,
                    estimatedShippingBusinessDays,
                    estimatedShippingDate,
                    quoteId,
                  }}
                  onSelect={handleFreightQuoteSelect}
                  onRequestManualEntry={handleManualFreightRequest}
                />
              )}
            />
          </Collapse>
        </Box>
      )}

      {isShippingCostVisible && (
        <Controller
          name="shippingCostValue"
          control={control}
          render={({ field, fieldState }) => (
            <MoneyField
              label={translate(LangEnum.GLOBAL_SHIPPING_COST)}
              disabled={!canEditOrder || isManualFreightReadOnly}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                setValue('freightOrigin', FreightOrigin.MANUAL);
              }}
              error={hasFieldError(fieldState, fieldsWithError, 'shippingCostValue')}
              helperText={fieldState.error?.message}
              onBlur={() => onSave()}
            />
          )}
        />
      )}
    </Stack>
  );
};

export default OrderConditionDeliveryFields;
